/**
 * Centralized Axios instance for FraudLens
 *
 * - Automatically attaches Authorization: Bearer <token> to every request
 * - Handles 401 responses by clearing stored credentials and triggering logout
 */
import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '/api',
});

// ─── Request Interceptor: Attach JWT ─────────────────────────────────────────

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('fraudlens_auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response Interceptor: Handle 401 ────────────────────────────────────────

/**
 * Logout callback registered by App.jsx via setAuthLogoutCallback().
 * Defaults to clearing localStorage so 401s before React mount still work.
 */
let _logoutCb = () => {
  localStorage.removeItem('fraudlens_auth_token');
  localStorage.removeItem('fraudlens_user');
};

export function setAuthLogoutCallback(fn) {
  _logoutCb = fn;
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only treat as "session expired" when we actually had a stored token.
      // If there is no token the 401 is expected (unauthenticated pre-login
      // request) and we must not call the logout callback — otherwise a
      // delayed 401 from a pre-login call could race with a fresh login and
      // immediately kill the new session.
      const token = localStorage.getItem('fraudlens_auth_token');
      if (token) {
        localStorage.removeItem('fraudlens_auth_token');
        localStorage.removeItem('fraudlens_user');
        if (_logoutCb) _logoutCb();
      }
    }
    return Promise.reject(error);
  }
);

export default api;
